import { useCallback, useState } from 'react';
import date from 'date-and-time';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedPresaveWidget } from 'fym-common/src/db/serializersTypes';
import { defaultPresaveServices } from 'fym-shared/src/smartlinks/presave';
import { useCreatePresaveCampaignForSmartLinkMutation, useUpdateWidgetMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { BlockAutosave } from '../forms/useBlockAutosave';
import { useEditorSaves } from '../saves/useEditorSaves';

interface UseTurnOnPresaveProps {
  accountId: number;
  smartLinkId: number;
  widget: SerializedPresaveWidget;
  autosave: BlockAutosave;
}

/**
 * "Let fans pre-save" on a Catalog links block with no campaign (design c1): two calls, the campaign
 * first (the existing pre-save create call of the page), then the block keeps the campaign uuid next
 * to its song.
 */
export function useTurnOnPresave({ accountId, smartLinkId, widget, autosave }: UseTurnOnPresaveProps) {
  const [createCampaign] = useCreatePresaveCampaignForSmartLinkMutation();
  const [updateWidget] = useUpdateWidgetMutation();
  const [isBusy, setBusy] = useState(false);
  const saves = useEditorSaves();
  const { markSaving, markSaved, markFailed } = autosave;

  const turnOn = useCallback(() => {
    const run = async (): Promise<boolean> => {
      setBusy(true);
      markSaving();
      const { trackTitle, artistName, releaseDate, catalogItemUuid } = widget.data;
      const created = await createCampaign({
        accountId,
        smartLinkId,
        title: trackTitle,
        artistName,
        // A day, stored at midnight UTC.
        releaseDate: date.format(new Date(releaseDate), 'YYYY-MM-DD', true),
      });
      if (!isSuccessfulResult(created)) {
        markFailed();
        return false;
      }
      const services = widget.data.services.length > 0 ? widget.data.services : defaultPresaveServices;
      const saved = await updateWidget({
        accountId,
        smartLinkId,
        widgetUUID: widget.uuid,
        position: widget.position,
        type: SmartLinkWidgetType.presave,
        data:
          typeof catalogItemUuid === 'string'
            ? { campaignUuid: created.data.campaign.uuid, catalogItemUuid, services }
            : { campaignUuid: created.data.campaign.uuid, services },
      });
      if (isSuccessfulResult(saved)) {
        markSaved(false);
        return true;
      }
      markFailed();
      return false;
    };
    // Both calls are one save for Publish: it waits until the block holds the campaign.
    const done = run();
    (saves?.track(done, (ok) => !ok) ?? done).catch(markFailed).finally(() => setBusy(false));
  }, [accountId, createCampaign, markFailed, markSaved, markSaving, saves, smartLinkId, updateWidget, widget]);

  return { turnOn, isBusy };
}
